import React from 'react';
import {
  HeadingText,
  ParagraphText,
  ProgressBar,
  StackLayout,
  useClickableCard,
} from '@leagueplatform/genesis-core';
import { useMasonryEngineActionEmitter } from '@leagueplatform/masonry-engine';
import { ConditionalLinkCardRenderer } from '@leagueplatform/masonry-renderers';
import { InternalOrExternalLink } from '@leagueplatform/web-common-components';
import {
  MasonryDeeplinkAction,
  MasonryProgressBarRendererProps,
} from 'common/masonry-types';

export const MasonryProgramProgressCardRenderer = ({
  className,
  currentText,
  engineClassName,
  heading,
  headingLevel = '3',
  onClick,
  progress,
  rootElementRef,
}: MasonryProgressBarRendererProps) => {
  const invokeAction = useMasonryEngineActionEmitter();
  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } =
    useClickableCard(rootElementRef);

  const url = (onClick as MasonryDeeplinkAction)?.payload.url;

  const handleClick = (event: React.MouseEvent) => {
    if (onClick) {
      invokeAction({ event, ...onClick });
    }
  };

  return (
    <ConditionalLinkCardRenderer
      hasLink={Boolean(url)}
      ref={clickAreaRef}
      onCardMouseUp={onCardMouseUp}
      onCardMouseDown={onCardMouseDown}
      // Carries the node's `theme.genesisTokenOverrides` — the engine emits them
      // as a Stitches class and expects the renderer to mount it.
      className={[engineClassName, className].filter(Boolean).join(' ')}
    >
      <StackLayout
        orientation="vertical"
        spacing="$threeQuarters"
        css={{ margin: '$oneAndHalf', alignItems: 'normal' }}
      >
        <HeadingText level={headingLevel} size="sm">
          {url ? (
            <InternalOrExternalLink
              ref={primaryActionRef}
              href={url}
              onClick={handleClick}
              // `&&` doubles specificity to beat the theme's anchor colour.
              css={{
                '&&': { color: 'inherit', textDecoration: 'none' },
                '&&:hover': { textDecoration: 'none' },
              }}
            >
              {heading}
            </InternalOrExternalLink>
          ) : (
            heading
          )}
        </HeadingText>
        <StackLayout
          orientation="vertical"
          spacing="$half"
          css={{ alignItems: 'normal', width: '100%' }}
        >
          <ParagraphText size="sm" css={{ marginBlockEnd: '$none' }}>
            {currentText}
          </ParagraphText>
          <ProgressBar
            fillProgressValue={progress}
            accessibilityLabel={currentText}
          />
        </StackLayout>
      </StackLayout>
    </ConditionalLinkCardRenderer>
  );
};
